import {
  mdiAutoFix,
  mdiCreation,
  mdiFolderImage,
  mdiHeartOutline,
  mdiImageMultipleOutline,
  mdiLightbulbOnOutline,
  mdiPencil
} from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import { AIConversationsList } from 'components/Composer/AIConversationsList/AIConversationsList'
import { SearchMode } from 'components/Composer/MediaSearchPanel'
import TextContentSearchPanel from 'components/Composer/TextContentSearchPanel'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAiImageRequestsLimit } from 'services/compose'
import { AIConversation, AIConversationKind } from 'services/compose/suggest/net'
import { contentSelectionSelector } from 'services/content/selectors'
import { StoreThunkDispatch } from 'store/state'
import { noop } from 'utils/noop'
import styles from './ComposerContentDialog.pcss'
import { EditTab } from './EditTab'
import { GenerateTab } from './GenerateTab'
import { SearchTab } from './SearchTab'

export type ComposerContentDialogTab = 'ai-content' | 'generate' | 'search' | 'edit' | 'ideas'

interface ComposerContentDialogProps {
  open: boolean
  initialTab?: ComposerContentDialogTab
  imageUrl?: string
  prompt?: string
  conversation?: AIConversation
  kind?: AIConversationKind
  searchMode?: SearchMode
  onClose: () => void
  onVideoUrlAdded?: () => void
}

const ICON_SIZE = '20px'

export function ComposerContentDialog(props: ComposerContentDialogProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [activeTab, setActiveTab] = useState<ComposerContentDialogTab>(props.initialTab || 'generate')
  const [searchMode, setSearchMode] = useState<SearchMode>(props.searchMode ?? 'stock')
  const [selectedConversation, setSelectedConversation] = useState<AIConversation | null>(null)
  const [selectedKind, setSelectedKind] = useState<AIConversationKind | undefined>(props.kind)

  const contentElementRef = useRef<HTMLDivElement>(null)
  const hasSelection = Object.keys(useSelector(contentSelectionSelector).items).length > 0

  useEffect(() => {
    dispatch(fetchAiImageRequestsLimit(true))
  }, [dispatch])

  useEffect(() => {
    if (props.open && props.initialTab) {
      setActiveTab(props.initialTab)
    }
    if (props.open) {
      setSelectedConversation(props.conversation || null)
      setSelectedKind(props.kind)
      setSearchMode(props.searchMode ?? 'stock')
    }
  }, [props.open, props.initialTab, props.conversation, props.kind, props.searchMode])

  const selectAIContent = () => setActiveTab('ai-content')
  const selectGenerate = () => {
    setSelectedConversation(null)
    setSelectedKind(undefined)
    setActiveTab('generate')
  }
  const selectEdit = () => setActiveTab('edit')
  const selectIdeas = () => setActiveTab('ideas')

  const handleConversationSelect = (conversation: AIConversation) => {
    setSelectedConversation(conversation)
    setActiveTab('generate')
  }

  const handleNewChat = (kind: AIConversationKind) => {
    setSelectedConversation(null)
    setSelectedKind(kind)
    setActiveTab('generate')
  }

  const selectSearchMode = (mode: SearchMode) => {
    setSearchMode(mode)
    setActiveTab('search')
  }
  const onSearchSubmit = () => setSearchMode('stock')

  return (
    <Dialog
      open={props.open}
      maxWidth={false}
      classes={{ paper: `${styles.dialog} ${hasSelection ? styles['dialog-above-drawer'] : ''}` }}
      onClose={props.onClose}
    >
      <div className={styles.tabs}>
        <Button
          className={`${styles.tab} ${activeTab === 'generate' ? styles['source-btn-active'] : ''}`}
          color="primary"
          startIcon={<Icon path={mdiAutoFix} size={ICON_SIZE} />}
          onClick={selectGenerate}
        >
          Generate with AI
        </Button>
        <div className={styles['search-controls']}>
          <Button
            className={`${styles.tab} ${activeTab === 'ai-content' ? styles['source-btn-active'] : ''}`}
            color="primary"
            startIcon={<Icon path={mdiCreation} size={ICON_SIZE} />}
            onClick={selectAIContent}
          >
            AI History
          </Button>
          <Button
            className={`${styles.tab} ${activeTab === 'search' && searchMode === 'library' ? styles['source-btn-active'] : ''}`}
            color="primary"
            startIcon={<Icon path={mdiFolderImage} size={ICON_SIZE} />}
            onClick={() => selectSearchMode('library')}
          >
            Library
          </Button>
          <Button
            className={`${styles.tab} ${styles['fav-btn']} 
            ${activeTab === 'search' && searchMode === 'favorites' ? styles['source-btn-active'] : ''}`}
            color="primary"
            startIcon={<Icon path={mdiHeartOutline} size={ICON_SIZE} />}
            onClick={() => selectSearchMode('favorites')}
          >
            Favorites
          </Button>
          <Button
            className={`${styles.tab} ${activeTab === 'search' && searchMode === 'stock' ? styles['source-btn-active'] : ''}`}
            color="primary"
            startIcon={<Icon path={mdiImageMultipleOutline} size={ICON_SIZE} />}
            onClick={() => selectSearchMode('stock')}
          >
            Stock media
          </Button>
          <Button
            className={`${styles.tab} ${activeTab === 'ideas' ? styles['source-btn-active'] : ''}`}
            color="primary"
            startIcon={<Icon path={mdiLightbulbOnOutline} size="16px" />}
            onClick={selectIdeas}
            data-testid="quotes-button"
          >
            Ideas
          </Button>
        </div>
        <div className={styles['tab-right']}>
          <Button
            className={`${styles.tab} ${activeTab === 'edit' ? styles['source-btn-active'] : ''}`}
            color="primary"
            startIcon={<Icon path={mdiPencil} size={ICON_SIZE} />}
            onClick={selectEdit}
          >
            Editor
          </Button>
        </div>
      </div>
      <div
        className={`${styles.content} ${activeTab !== 'generate' ? styles['content-fill'] : ''}`}
        ref={contentElementRef}
      >
        {activeTab === 'ai-content' && (
          <AIConversationsList
            className={styles.aiContentTab}
            onConversationSelect={handleConversationSelect}
            onNewChat={handleNewChat}
          />
        )}
        {activeTab === 'generate' && (
          <GenerateTab conversation={selectedConversation || undefined} prompt={props.prompt} kind={selectedKind} />
        )}
        {activeTab === 'search' && (
          <SearchTab
            onClose={props.onClose}
            onVideoUrlAdded={props.onVideoUrlAdded || noop}
            mode={searchMode}
            onSearchSubmit={onSearchSubmit}
          />
        )}
        {activeTab === 'edit' && <EditTab imageUrl={props.imageUrl} onClose={props.onClose} />}
        {activeTab === 'ideas' && (<TextContentSearchPanel scrollElement={contentElementRef.current} />)}
      </div>
    </Dialog>
  )
}

export default ComposerContentDialog
